// frontend/src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';

interface User {
  id: string;
  name: string;
  email?: string;
  role: 'user' | 'admin' | 'anonymous';
  isAnonymous?: boolean;
  picture?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  googleLogin: () => void;
  continueAsGuest: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const isAnonymous = localStorage.getItem('isAnonymous') === 'true';
      if (isAnonymous) {
        setUser({
          id: 'anonymous',
          name: 'Anonymous User',
          role: 'anonymous',
          isAnonymous: true
        });
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('prayerbridge_token');
      if (token) {
        const response = await api.auth.validateToken();
        setUser(response.data.user);
      }
    } catch (err) {
      localStorage.removeItem('prayerbridge_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.auth.login({ email, password });
      localStorage.setItem('prayerbridge_token', response.data.token);
      localStorage.removeItem('isAnonymous');
      setUser(response.data.user);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to login');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.auth.register({ name, email, password });
      localStorage.setItem('prayerbridge_token', response.data.token);
      localStorage.removeItem('isAnonymous');
      setUser(response.data.user);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to register');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await api.auth.logout();
      localStorage.removeItem('prayerbridge_token');
      localStorage.removeItem('isAnonymous');
      setUser(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to logout');
    }
  };

  const googleLogin = () => {
    window.location.href = `${import.meta.env.VITE_GOOGLE_AUTH_URL}`;
  };

  const continueAsGuest = () => {
    const anonymousUser: User = {
      id: 'anonymous',
      name: 'Anonymous User',
      role: 'anonymous',
      isAnonymous: true
    };
    setUser(anonymousUser);
    localStorage.setItem('isAnonymous', 'true');
    localStorage.removeItem('prayerbridge_token');
  };

  const clearError = () => setError(null);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        register,
        logout,
        googleLogin,
        continueAsGuest,
        clearError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};